/*
 **	Mobile WebSocket Service for Real-time Device Updates
 **	OMNI IQ Mobile (c) 2025 Silver Falls Capital. All Rights Reserved
 */

export interface WebSocketMessage {
  type: string
  state?: {
    state?: {
      [deviceId: string]: {
        data: any
        events: any[]
      }
    }
  }
}

export interface DeviceDataFromWebSocket {
  // Primary Data Fields
  dp0?: number
  dp1?: number
  
  // Threshold Configuration
  dp0wt?: number  // Diff Pressure 0 Warning Threshold
  dp1wt?: number  // Diff Pressure 1 Warning Threshold  
  dp0at?: number  // Diff Pressure 0 Alarm Threshold
  dp1at?: number  // Diff Pressure 1 Alarm Threshold
  
  // Connection Status
  w_rssi?: number  // WiFi RSSI
  c_rssi?: number  // Cellular RSSI
  w_connected?: number  // WiFi Connected (1/0)
  c_connected?: number  // Cellular Connected (1/0)
  
  // Power Status
  on_batt?: number  // On Battery (1/0)
  on_power?: number  // On Connected Power (1/0)
  batt_percent?: number  // Battery Percentage
  
  // Device Configuration
  zone1_name?: string  // Zone 1 Configured Name
  zone2_name?: string  // Zone 2 Configured Name
  measure_unit?: number  // Measurement Units
  measure_sec?: number  // Measurement Seconds
  auto_z_sec?: number  // Auto Zero Seconds
  uptime_sec?: number  // Uptime Seconds
  
  // Bridge Connectivity
  br_ping_ct?: number  // Bridge Ping Count
  br_bad_ping_ct?: number  // Bridge Bad Ping Count
  br_bad_ping_seq_ct?: number  // Bridge Bad Ping Sequence Count
  br_longest_bad_ping_seq_ct?: number  // Bridge Longest Bad Ping Count
  
  // Alarm States
  dp0_alarm_state?: number  // Differential Pressure 0 Alarm State
  dp1_alarm_state?: number  // Differential Pressure 1 Alarm State
  
  timestamp: string
}

// WebSocket connection for the user's namespace
interface WebSocketConnection {
  ws: WebSocket | null
  url: string
  reconnectAttempts: number
  reconnectTimeout: NodeJS.Timeout | null
  isConnected: boolean
}

export class WebSocketService {
  private connection: WebSocketConnection | null = null
  private token: string = ''
  private namespace: string = ''
  private agentId: string = '' // Will be set to user's namespace ID
  private maxReconnectAttempts: number = 5
  private isIntentionallyClosed: boolean = false
  private baseUrl: string = import.meta.env.VITE_STRATA47_WEBSOCKET_URL || 'wss://test-rt.strata47.com'
  
  // Callbacks
  private onDeviceDataUpdate?: (deviceId: string, data: DeviceDataFromWebSocket, events: any[]) => void
  private onConnectionStateChange?: (connected: boolean) => void
  private onError?: (error: Error) => void

  constructor() {
    this.handleMessage = this.handleMessage.bind(this)
    this.handleOpen = this.handleOpen.bind(this)
    this.handleClose = this.handleClose.bind(this)
    this.handleError = this.handleError.bind(this)
  }

  // Encode token for WebSocket authentication (same pattern as existing system)
  private encodeToken(token: string): string {
    const type = token.split("|")[0]
    const value = token.split("|")[1]
    if (type === "user") {
      return btoa("x-usertoken|" + value)
    } else if (type === "auth0") {
      return btoa("authorization|" + value)
    } else {
      return btoa("x-apitoken|" + value)
    }
  }

  // Connect using the same pattern as frontend (with deviceTypeId)
  connect(namespace: string, token: string, agentId: string): void {
    this.namespace = namespace
    this.token = token
    this.agentId = agentId
    this.isIntentionallyClosed = false
    
    console.log(`🔌 WebSocketService: Connecting with agent ID: ${agentId}`)
    
    this.connectToNamespace()
  }

  private connectToNamespace(): void {
    try {
      // Build WebSocket URL for user's primary namespace
      const wsUrl = new URL(this.baseUrl)
      wsUrl.pathname = `/agents/s47-latest-data/${this.agentId}/websocket`
      wsUrl.searchParams.append('namespaceName', this.namespace)
      wsUrl.searchParams.append('token', this.encodeToken(this.token))
      wsUrl.searchParams.append('authtype', 'ws')
      wsUrl.searchParams.append('params', 'true')
      
      const url = wsUrl.toString()
      
      console.log(`🔌 WebSocketService: Connecting to user's primary namespace`)
      console.log(`🔌 WebSocketService: URL: ${url}`)
      console.log(`🔌 WebSocketService: Agent ID (Primary Namespace): ${this.agentId}`)
      console.log(`🔌 WebSocketService: Namespace Name: ${this.namespace}`)
      console.log(`🔌 WebSocketService: Token prefix: ${this.token.substring(0, 10)}...`)
      
      const ws = new WebSocket(url)
      
      // Create connection object
      this.connection = {
        ws,
        url,
        reconnectAttempts: 0,
        reconnectTimeout: null,
        isConnected: false
      }
      
      // Bind event handlers
      ws.addEventListener('open', () => this.handleOpen())
      ws.addEventListener('message', (event) => this.handleMessage(event))
      ws.addEventListener('close', (event) => this.handleClose(event))
      ws.addEventListener('error', (event) => this.handleError(event))
      
    } catch (error) {
      console.error(`🔌 WebSocketService: Failed to create WebSocket connection:`, error)
      this.onError?.(error instanceof Error ? error : new Error('WebSocket connection failed'))
    }
  }


  disconnect(): void {
    this.isIntentionallyClosed = true
    
    console.log(`🔌 WebSocketService: Disconnecting WebSocket`)
    
    if (this.connection) {
      if (this.connection.reconnectTimeout) {
        clearTimeout(this.connection.reconnectTimeout)
        this.connection.reconnectTimeout = null
      }
      
      if (this.connection.ws) {
        this.connection.ws.close()
        this.connection.ws = null
      }
      
      this.connection = null
    }
    
    this.onConnectionStateChange?.(false)
  }

  private handleOpen(): void {
    if (!this.connection) return
    
    console.log(`🔌 WebSocketService: Connected successfully`)
    this.connection.reconnectAttempts = 0
    this.connection.isConnected = true
    
    this.onConnectionStateChange?.(true)
    
    console.log(`🔌 WebSocketService: Connection established, ready to receive device data`)
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const message: WebSocketMessage = JSON.parse(event.data)
      
      console.log(`🔌 WebSocketService: Received message:`, message.type)
      
      if (message.type === 'cf_agent_state' && message.state?.state) {
        const deviceCount = Object.keys(message.state.state).length
        console.log(`🔌 WebSocketService: Processing ${deviceCount} device updates`)
        
        // Process device state updates
        Object.entries(message.state.state).forEach(([deviceId, deviceState]) => {
          if (deviceState.data) {
            console.log(`🔌 WebSocketService: Device ${deviceId} data:`, deviceState.data)
            this.onDeviceDataUpdate?.(deviceId, deviceState.data, deviceState.events || [])
          } else {
            console.log(`🔌 WebSocketService: No data for device ${deviceId}`)
          }
        })
      } else {
        console.log(`🔌 WebSocketService: Ignoring message type: ${message.type}`)
      }
      
    } catch (error) {
      console.error(`🔌 WebSocketService: Failed to parse message:`, error)
      console.error(`🔌 WebSocketService: Raw message data:`, event.data)
    }
  }

  private handleClose(event: CloseEvent): void {
    if (!this.connection) return
    
    console.log(`🔌 WebSocketService: Connection closed`)
    console.log(`🔌 WebSocketService: Close code: ${event.code}`)
    console.log(`🔌 WebSocketService: Close reason: ${event.reason || 'No reason provided'}`)
    console.log(`🔌 WebSocketService: Was clean: ${event.wasClean}`)
    this.connection.isConnected = false
    
    this.onConnectionStateChange?.(false)
    
    // Attempt reconnection if not intentionally closed
    if (!this.isIntentionallyClosed && this.connection.reconnectAttempts < this.maxReconnectAttempts) {
      this.connection.reconnectAttempts++
      const delay = Math.min(1000 * Math.pow(2, this.connection.reconnectAttempts), 30000) // Exponential backoff, max 30s
      
      console.log(`🔌 WebSocketService: Attempting reconnection ${this.connection.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`)
      
      this.connection.reconnectTimeout = setTimeout(() => {
        if (!this.isIntentionallyClosed) {
          this.connectToNamespace()
        }
      }, delay)
    } else if (this.connection.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(`🔌 WebSocketService: Max reconnection attempts reached`)
      this.onError?.(new Error('WebSocket connection failed after multiple attempts'))
    }
  }

  private handleError(event: Event): void {
    // Capture connection info before it might be cleared
    const connectionUrl = this.connection?.url || 'URL not available'
    const readyState = this.connection?.ws?.readyState
    const readyStateText = readyState !== undefined ? 
      ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][readyState] || readyState.toString() : 
      'Unknown'
    
    console.error(`🔌 WebSocketService: WebSocket error:`, event)
    console.error(`🔌 WebSocketService: Connection URL: ${connectionUrl}`)
    console.error(`🔌 WebSocketService: Ready state: ${readyStateText} (${readyState})`)
    
    // Only process error if not intentionally closed
    if (!this.isIntentionallyClosed) {
      // Provide more specific error information
      let errorMessage = 'WebSocket connection error'
      if (readyState === WebSocket.CONNECTING) {
        errorMessage += ' (failed to connect)'
      } else if (readyState === WebSocket.CLOSED) {
        errorMessage += ' (connection closed unexpectedly)'
      }
      
      this.onError?.(new Error(errorMessage))
    }
  }

  // Event handlers
  onDeviceData(callback: (deviceId: string, data: DeviceDataFromWebSocket, events: any[]) => void): void {
    this.onDeviceDataUpdate = callback
  }

  onConnectionChange(callback: (connected: boolean) => void): void {
    this.onConnectionStateChange = callback
  }

  onErrorOccurred(callback: (error: Error) => void): void {
    this.onError = callback
  }

  isConnected(): boolean {
    return this.connection?.ws?.readyState === WebSocket.OPEN && this.connection.isConnected === true
  }

  getConnectionStatus(): boolean {
    return this.isConnected()
  }

  getReadyState(): number | null {
    return this.connection?.ws?.readyState ?? null
  }
}

// Singleton instance
export const webSocketService = new WebSocketService()